import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FeatureCollection, Geometry } from 'geojson';
import {
    GeoMapCountrySelection,
    GeoMapFeature,
    GeoMapFeatureCollection,
    GeoMapFill,
    GeoMapRegionFeature,
    GeoMapRegionFeatureCollection,
    GeoMapRegionSelection
} from '../../data-access/geographical-map.types';

declare const d3: any;

@Component({
    selector: 'app-eic-d3-map',
    templateUrl: './eic-d3-map.component.html',
    styleUrls: ['./eic-d3-map.component.scss']
})
export class EicD3MapComponent implements OnChanges {
    @Input() geometry: GeoMapFeatureCollection = { type: 'FeatureCollection', features: [] };
    @Input() regionGeometry: GeoMapRegionFeatureCollection = { type: 'FeatureCollection', features: [] };
    @Input() drilldownGeometry: GeoMapFeatureCollection | null = null;
    @Input() nonInteractiveCountryIds: number[] = [];
    @Input() fills: GeoMapFill[] = [];
    @Input() selectedRegionId: number | null = null;
    @Input() selectedCountryId: number | null = null;

    @Output() regionSelected = new EventEmitter<GeoMapRegionSelection>();
    @Output() countrySelected = new EventEmitter<GeoMapCountrySelection>();

    private readonly viewWidth = 1000;
    private readonly viewHeight = 520;
    private readonly regionViewportPadding = 18;
    private readonly countryViewportPadding = 8;

    fillLookup = new Map<number, string>();
    projectedFeatures: Array<{ feature: GeoMapFeature; path: string }> = [];
    projectedCountryLabels: Array<{ name: string; x: number; y: number }> = [];
    projectedRegionFeatures: Array<{ feature: GeoMapRegionFeature; path: string }> = [];
    projectedRegionLabels: Array<{ eicRegionId: number; name: string; lines: string[]; x: number; y: number }> = [];
    hoveredCountry: { name: string; x: number; y: number } | null = null;
    hoveredRegionId: number | null = null;

    get showCountryLayer(): boolean {
        return this.hasDrilldownGeometry || !!this.selectedRegionId;
    }

    get showRegionLayer(): boolean {
        return !this.showCountryLayer;
    }

    private get hasDrilldownGeometry(): boolean {
        return Array.isArray(this.drilldownGeometry?.features) && this.drilldownGeometry!.features.length > 0;
    }

    get hasVisibleMapData(): boolean {
        return this.showCountryLayer ? this.projectedFeatures.length > 0 : this.projectedRegionFeatures.length > 0;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.fills) {
            this.fillLookup = new Map(this.fills.map((fill) => [fill.eicCountryId, fill.fill]));
        }

        if (changes.geometry || changes.selectedRegionId || changes.drilldownGeometry) {
            const drilldownGeometry = this.getDrilldownGeometry();
            this.projectedFeatures = this.toProjectedFeatures(drilldownGeometry);
            this.projectedCountryLabels = this.toProjectedCountryLabels(drilldownGeometry);
        }

        if (changes.regionGeometry || changes.selectedRegionId) {
            this.projectedRegionFeatures = this.toProjectedRegionFeatures(this.getWorldRegionGeometry());
            this.projectedRegionLabels = this.toProjectedRegionLabels(this.getWorldRegionGeometry());
        }
    }

    onFeatureClicked(feature: GeoMapFeature): void {
        if (!feature?.properties) {
            return;
        }

        if (!this.isCountryInteractive(feature)) {
            return;
        }

        this.countrySelected.emit({
            eicCountryId: feature.properties.eicCountryId,
            eicRegionId: feature.properties.eicRegionId,
            sid: feature.properties.sid,
            countryName: feature.properties.name
        });
    }

    onCountryHovered(feature: GeoMapFeature, event: MouseEvent): void {
        if (!feature?.properties?.name) {
            return;
        }

        this.hoveredCountry = {
            name: feature.properties.name,
            x: event.offsetX,
            y: event.offsetY
        };
    }

    clearCountryHover(): void {
        this.hoveredCountry = null;
    }

    onRegionFeatureClicked(feature: GeoMapRegionFeature): void {
        if (!feature?.properties?.eicRegionId) {
            return;
        }

        this.regionSelected.emit({ eicRegionId: feature.properties.eicRegionId });
    }

    onRegionHovered(feature: GeoMapRegionFeature): void {
        const regionId = Number(feature?.properties?.eicRegionId) || 0;
        this.hoveredRegionId = regionId > 0 ? regionId : null;
    }

    clearRegionHover(): void {
        this.hoveredRegionId = null;
    }

    private getDrilldownGeometry(): GeoMapFeatureCollection {
        if (this.hasDrilldownGeometry) {
            return this.drilldownGeometry as GeoMapFeatureCollection;
        }

        if (!this.selectedRegionId) {
            return { type: 'FeatureCollection', features: [] };
        }

        const regionFeatures = (this.geometry?.features || []).filter(
            (feature) => feature?.properties?.eicRegionId === this.selectedRegionId
        );

        return {
            type: 'FeatureCollection',
            features: regionFeatures
        };
    }

    private getWorldRegionGeometry(): GeoMapRegionFeatureCollection {
        if (this.showCountryLayer) {
            return { type: 'FeatureCollection', features: [] };
        }

        return this.regionGeometry || { type: 'FeatureCollection', features: [] };
    }

    getFill(feature: GeoMapFeature): string {
        const countryId = feature?.properties?.eicCountryId;
        if (!countryId) {
            return 'var(--map-land)';
        }

        if (countryId === this.selectedCountryId) {
            return 'var(--map-country-highlight)';
        }

        return 'var(--map-land)';
    }

    getRegionFill(feature: GeoMapRegionFeature): string {
        const regionId = Number(feature?.properties?.eicRegionId) || 0;
        if (regionId > 0 && regionId === this.hoveredRegionId) {
            return 'var(--map-region-highlight)';
        }

        return 'var(--map-land)';
    }

    isCountryInteractive(feature: GeoMapFeature): boolean {
        const countryId = Number(feature?.properties?.eicCountryId) || 0;
        return countryId <= 0 || !this.nonInteractiveCountryIds.includes(countryId);
    }

    private toProjectedFeatures(geometry: GeoMapFeatureCollection): Array<{ feature: GeoMapFeature; path: string }> {
        // Exclude Antarctica by name, admin, or adm0_a3
        const validFeatures = (geometry?.features || []).filter((feature) => {
            if (!feature?.geometry || !feature?.properties) return false;
            if (!this.hasRenderableRings(feature.geometry)) return false;
            const props = feature.properties;
            const name = (props.name || '').toLowerCase();
            const sid = (props.sid || '').toUpperCase();
            // Hide Antarctica by name or ISO2 code
            if (name === 'antarctica' || sid === 'AQ') return false;
            return true;
        });
        if (!validFeatures.length) {
            return [];
        }

        const rings = validFeatures.flatMap((feature) => this.extractRings(feature.geometry));
        if (this.looksLikeGeographicCoordinates(rings)) {
            const featureCollection: FeatureCollection<Geometry, any> = {
                type: 'FeatureCollection',
                features: validFeatures as any[]
            };
            const pathBuilder = this.createEquirectangularPathBuilder(featureCollection, this.countryViewportPadding);
            if (pathBuilder) {
                return validFeatures
                    .map((feature) => ({
                        feature,
                        path: pathBuilder(feature as any) || ''
                    }))
                    .filter((entry) => !!entry.path);
            }
        }

        const projectedSet = this.createProjectedGeometrySet(
            validFeatures.map((feature) => feature.geometry),
            this.countryViewportPadding
        );
        if (!projectedSet) {
            return [];
        }

        return validFeatures
            .map((feature, index) => ({
                feature,
                path: this.buildPathFromRings(projectedSet.ringsByGeometry[index], projectedSet.transform)
            }))
            .filter((entry) => !!entry.path);
    }

    private toProjectedRegionFeatures(geometry: GeoMapRegionFeatureCollection): Array<{ feature: GeoMapRegionFeature; path: string }> {
        // Exclude Antarctica by name, admin, or adm0_a3
        const validFeatures = (geometry?.features || []).filter((feature) => {
            if (!feature?.geometry || !feature?.properties) return false;
            if (!this.hasRenderableRings(feature.geometry)) return false;
            const props = feature.properties;
            const name = (props.name || '').toLowerCase();
            const admin = ('admin' in props && typeof props.admin === 'string') ? props.admin.toLowerCase() : '';
            const adm0_a3 = ('adm0_a3' in props && typeof props.adm0_a3 === 'string') ? props.adm0_a3.toUpperCase() : '';
            if (name === 'antarctica' || admin === 'antarctica' || adm0_a3 === 'ATA') return false;
            return true;
        });
        if (!validFeatures.length) {
            return [];
        }

        const rings = validFeatures.flatMap((feature) => this.extractRings(feature.geometry));
        if (this.looksLikeGeographicCoordinates(rings)) {
            const featureCollection: FeatureCollection<Geometry, any> = {
                type: 'FeatureCollection',
                features: validFeatures as any[]
            };
            const pathBuilder = this.createEquirectangularPathBuilder(featureCollection, this.regionViewportPadding);
            if (pathBuilder) {
                return validFeatures
                    .map((feature) => ({
                        feature,
                        path: pathBuilder(feature as any) || ''
                    }))
                    .filter((entry) => !!entry.path);
            }
        }

        const projectedSet = this.createProjectedGeometrySet(
            validFeatures.map((feature) => feature.geometry),
            this.regionViewportPadding
        );
        if (!projectedSet) {
            return [];
        }

        return validFeatures
            .map((feature, index) => ({
                feature,
                path: this.buildPathFromRings(projectedSet.ringsByGeometry[index], projectedSet.transform)
            }))
            .filter((entry) => !!entry.path);
    }

    private toProjectedRegionLabels(geometry: GeoMapRegionFeatureCollection): Array<{ eicRegionId: number; name: string; lines: string[]; x: number; y: number }> {
        const validFeatures = (geometry?.features || []).filter(
            (feature) => feature?.geometry && feature?.properties && this.hasRenderableRings(feature.geometry)
        );
        if (!validFeatures.length) {
            return [];
        }

        const rings = validFeatures.flatMap((feature) => this.extractRings(feature.geometry));
        if (this.looksLikeGeographicCoordinates(rings)) {
            const featureCollection: FeatureCollection<Geometry, any> = {
                type: 'FeatureCollection',
                features: validFeatures as any[]
            };
            const pathBuilder = this.createEquirectangularPathBuilder(featureCollection, this.regionViewportPadding);
            if (pathBuilder) {
                return validFeatures.map((feature) => {
                    const centroid = pathBuilder.centroid(feature as any) || [0, 0];
                    const eicRegionId = Number(feature?.properties?.eicRegionId) || 0;
                    const name = this.getDisplayRegionLabel(String(feature?.properties?.name || '').trim(), eicRegionId);
                    const lines = this.getRegionLabelLines(name);
                    const offset = this.getRegionLabelOffset(eicRegionId, name);

                    return {
                        eicRegionId,
                        name,
                        lines,
                        x: (Number(centroid[0]) || 0) + offset.x,
                        y: (Number(centroid[1]) || 0) + offset.y
                    };
                }).filter((label) => !!label.name);
            }
        }

        const projectedSet = this.createProjectedGeometrySet(
            validFeatures.map((feature) => feature.geometry),
            this.regionViewportPadding
        );
        if (!projectedSet) {
            return [];
        }

        return validFeatures.map((feature, index) => {
            const center = this.getProjectedLabelCenter(projectedSet.ringsByGeometry[index], projectedSet.transform);
            const eicRegionId = Number(feature?.properties?.eicRegionId) || 0;
            const name = this.getDisplayRegionLabel(String(feature?.properties?.name || '').trim(), eicRegionId);
            const lines = this.getRegionLabelLines(name);
            const offset = this.getRegionLabelOffset(eicRegionId, name);

            return {
                eicRegionId,
                name,
                lines,
                x: center.x + offset.x,
                y: center.y + offset.y
            };
        }).filter((label) => !!label.name);
    }

    private toProjectedCountryLabels(geometry: GeoMapFeatureCollection): Array<{ name: string; x: number; y: number }> {
        const validFeatures = (geometry?.features || []).filter(
            (feature) => feature?.geometry && feature?.properties?.name && this.hasRenderableRings(feature.geometry)
        );
        if (!validFeatures.length) {
            return [];
        }

        const rings = validFeatures.flatMap((feature) => this.extractRings(feature.geometry));
        if (this.looksLikeGeographicCoordinates(rings)) {
            const featureCollection: FeatureCollection<Geometry, any> = {
                type: 'FeatureCollection',
                features: validFeatures as any[]
            };
            const pathBuilder = this.createEquirectangularPathBuilder(featureCollection, this.countryViewportPadding);
            if (pathBuilder) {
                return validFeatures.map((feature) => {
                    const centroid = pathBuilder.centroid(feature as any) || [0, 0];
                    return {
                        name: String(feature?.properties?.name || '').trim(),
                        x: Number(centroid[0]) || 0,
                        y: Number(centroid[1]) || 0
                    };
                }).filter((label) => !!label.name);
            }
        }

        const projectedSet = this.createProjectedGeometrySet(
            validFeatures.map((feature) => feature.geometry),
            this.countryViewportPadding
        );
        if (!projectedSet) {
            return [];
        }

        return validFeatures.map((feature, index) => {
            const center = this.getProjectedLabelCenter(projectedSet.ringsByGeometry[index], projectedSet.transform);
            return {
                name: String(feature?.properties?.name || '').trim(),
                x: center.x,
                y: center.y
            };
        }).filter((label) => !!label.name);
    }

    private getDisplayRegionLabel(name: string, eicRegionId: number): string {
        const normalised = (name || '').trim().toLowerCase();
        if (eicRegionId === 9 || normalised === 'oceania') {
            return 'Australasia';
        }

        if (eicRegionId === 5 || normalised === 'indian subcontinent' || normalised === 'indian-subcontinent') {
            return 'Indian-Subcontinent';
        }

        return name;
    }

    private getRegionLabelLines(name: string): string[] {
        const value = (name || '').trim();
        if (!value) {
            return [];
        }

        const hyphenIndex = value.indexOf('-');
        if (hyphenIndex > 0 && hyphenIndex < value.length - 1) {
            const firstLine = value.slice(0, hyphenIndex + 1);
            const secondLine = value.slice(hyphenIndex + 1);
            return [firstLine, secondLine];
        }

        return [value];
    }

    private getRegionLabelOffset(eicRegionId: number, label: string): { x: number; y: number } {
        const normalised = (label || '').trim().toLowerCase();

        if (eicRegionId === 11 || normalised === 'north america') {
            return { x: 0, y: 18 };
        }

        if (eicRegionId === 3 || normalised === 'europe') {
            return { x: -28, y: 22 };
        }

        if (eicRegionId === 5 || normalised === 'indian-subcontinent') {
            return { x: 0, y: 12 };
        }

        return { x: 0, y: 0 };
    }

    private toProjectedFeaturesCartesian(features: GeoMapFeature[]): Array<{ feature: GeoMapFeature; path: string }> {
        const projectedSet = this.createProjectedGeometrySet(
            features.map((feature) => feature.geometry),
            this.countryViewportPadding
        );
        if (!projectedSet) {
            return [];
        }

        return features
            .map((feature, index) => ({
                feature,
                path: this.buildPathFromRings(projectedSet.ringsByGeometry[index], projectedSet.transform)
            }))
            .filter((entry) => !!entry.path);
    }

    private toProjectedRegionFeaturesCartesian(features: GeoMapRegionFeature[]): Array<{ feature: GeoMapRegionFeature; path: string }> {
        const projectedSet = this.createProjectedGeometrySet(
            features.map((feature) => feature.geometry),
            this.regionViewportPadding
        );
        if (!projectedSet) {
            return [];
        }

        return features
            .map((feature, index) => ({
                feature,
                path: this.buildPathFromRings(projectedSet.ringsByGeometry[index], projectedSet.transform)
            }))
            .filter((entry) => !!entry.path);
    }

    private createProjectedGeometrySet(geometries: Geometry[], padding: number): {
        ringsByGeometry: number[][][][];
        transform: {
            toX: (lng: number) => number;
            toY: (lat: number) => number;
        };
    } | null {
        const ringsByGeometry = geometries.map((geometry) => this.extractRings(geometry));
        const allRings = ringsByGeometry.flat();
        if (!allRings.length) {
            return null;
        }

        const normalizedRingsByGeometry = this.looksLikeGeographicCoordinates(allRings)
            ? this.unwrapGeographicRings(ringsByGeometry)
            : ringsByGeometry;
        const transform = this.getCollectionTransformFromRings(normalizedRingsByGeometry.flat(), padding);
        if (!transform) {
            return null;
        }

        return {
            ringsByGeometry: normalizedRingsByGeometry,
            transform
        };
    }

    private unwrapGeographicRings(ringsByGeometry: number[][][][]): number[][][][] {
        const longitudes = ringsByGeometry.flat(2).map((point) => this.normalizeLongitude(point[0]));
        const cutLongitude = this.getProjectionCutLongitude(longitudes);

        return ringsByGeometry.map((rings) =>
            rings.map((ring) =>
                ring.map((point) => [this.unwrapLongitude(point[0], cutLongitude), point[1]])
            )
        );
    }

    private getProjectionCutLongitude(longitudes: number[]): number {
        if (!longitudes.length) {
            return -180;
        }

        const sorted = [...longitudes].sort((left, right) => left - right);
        let largestGap = -1;
        let cutLongitude = sorted[0];

        for (let index = 0; index < sorted.length; index += 1) {
            const current = sorted[index];
            const next = index === sorted.length - 1 ? sorted[0] + 360 : sorted[index + 1];
            const gap = next - current;

            if (gap > largestGap) {
                largestGap = gap;
                cutLongitude = this.normalizeLongitude(current + (gap / 2));
            }
        }

        return cutLongitude;
    }

    private normalizeLongitude(longitude: number): number {
        const normalized = ((longitude + 180) % 360 + 360) % 360 - 180;
        return normalized === -180 ? 180 : normalized;
    }

    private createEquirectangularPathBuilder(
        featureCollection: FeatureCollection<Geometry, any>,
        padding: number
    ): any | null {
        if (!(typeof d3 !== 'undefined' && d3.geo && d3.geo.equirectangular && d3.geo.path)) {
            return null;
        }

        const rings = (featureCollection.features || []).flatMap((feature) => this.extractRings((feature as any).geometry));
        if (!rings.length) {
            return null;
        }

        const longitudes = rings.flat().map((point) => this.normalizeLongitude(point[0]));
        if (!longitudes.length) {
            return null;
        }

        const cutLongitude = this.getProjectionCutLongitude(longitudes);
        const centerLongitude = this.normalizeLongitude(cutLongitude - 180);

        const projection = d3.geo.equirectangular();
        projection.center([centerLongitude, 0]);
        projection.scale(1).translate([0, 0]);

        const pathBuilder = d3.geo.path().projection(projection);
        const bounds = pathBuilder.bounds(featureCollection);
        if (!this.hasValidProjectionBounds(bounds)) {
            return null;
        }

        const dx = bounds[1][0] - bounds[0][0];
        const dy = bounds[1][1] - bounds[0][1];
        if (dx <= 0 || dy <= 0) {
            return null;
        }

        const availableWidth = Math.max(this.viewWidth - (padding * 2), 1);
        const availableHeight = Math.max(this.viewHeight - (padding * 2), 1);
        const scale = 1 / Math.max(dx / availableWidth, dy / availableHeight, 1e-6);
        const translate = [
            padding + ((availableWidth - (scale * dx)) / 2) - (scale * bounds[0][0]),
            padding + ((availableHeight - (scale * dy)) / 2) - (scale * bounds[0][1])
        ];

        projection.scale(scale).translate(translate);
        return d3.geo.path().projection(projection);
    }

    private unwrapLongitude(longitude: number, cutLongitude: number): number {
        let unwrapped = this.normalizeLongitude(longitude);
        while (unwrapped < cutLongitude) {
            unwrapped += 360;
        }

        return unwrapped;
    }

    private getCollectionTransformFromRings(rings: number[][][], padding: number): {
        toX: (lng: number) => number;
        toY: (lat: number) => number;
    } | null {
        const allPoints = rings.flat();
        if (!allPoints.length) {
            return null;
        }

        const xs = allPoints.map((point) => point[0]);
        const ys = allPoints.map((point) => point[1]);
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);

        const sourceWidth = Math.max(maxX - minX, 1e-6);
        const sourceHeight = Math.max(maxY - minY, 1e-6);
        const targetWidth = Math.max(this.viewWidth - (padding * 2), 1);
        const targetHeight = Math.max(this.viewHeight - (padding * 2), 1);
        const scale = Math.min(targetWidth / sourceWidth, targetHeight / sourceHeight);

        const scaledWidth = sourceWidth * scale;
        const scaledHeight = sourceHeight * scale;
        const xOffset = padding + ((targetWidth - scaledWidth) / 2);
        const yOffset = padding + ((targetHeight - scaledHeight) / 2);

        return {
            toX: (value: number): number => xOffset + (value - minX) * scale,
            toY: (value: number): number => yOffset + (maxY - value) * scale
        };
    }

    private buildPathFromRings(
        rings: number[][][],
        transform: { toX: (lng: number) => number; toY: (lat: number) => number }
    ): string {
        if (!rings.length) {
            return '';
        }

        return rings
            .map((ring) => ring.map((coord, index) => `${index === 0 ? 'M' : 'L'}${transform.toX(coord[0])},${transform.toY(coord[1])}`).join(' ') + ' Z')
            .join(' ');
    }

    private getProjectedLabelCenter(
        rings: number[][][],
        transform: { toX: (lng: number) => number; toY: (lat: number) => number }
    ): { x: number; y: number } {
        const projectedPoints = rings.flat().map((point) => ({
            x: transform.toX(point[0]),
            y: transform.toY(point[1])
        }));
        if (!projectedPoints.length) {
            return { x: 0, y: 0 };
        }

        const count = projectedPoints.length;
        return {
            x: projectedPoints.reduce((sum, point) => sum + point.x, 0) / count,
            y: projectedPoints.reduce((sum, point) => sum + point.y, 0) / count
        };
    }

    private looksLikeGeographicCoordinates(rings: number[][][]): boolean {
        const points = rings.flat();
        if (!points.length) {
            return false;
        }

        return points.every((point) => {
            const lng = point[0];
            const lat = point[1];
            return Number.isFinite(lng) && Number.isFinite(lat) && lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90;
        });
    }

    private hasRenderableRings(geometry: Geometry): boolean {
        const rings = this.extractRings(geometry);
        if (!rings.length) {
            return false;
        }

        return rings.some((ring) =>
            Array.isArray(ring)
            && ring.length >= 3
            && ring.every((point) =>
                Array.isArray(point)
                && point.length >= 2
                && Number.isFinite(Number(point[0]))
                && Number.isFinite(Number(point[1]))
            )
        );
    }

    private hasValidProjectionBounds(bounds: any): boolean {
        if (!Array.isArray(bounds) || bounds.length !== 2) {
            return false;
        }

        const [start, end] = bounds;
        if (!Array.isArray(start) || !Array.isArray(end) || start.length < 2 || end.length < 2) {
            return false;
        }

        return [start[0], start[1], end[0], end[1]].every((value) => Number.isFinite(Number(value)));
    }

    private buildFallbackPath(geometry: Geometry): string {
        const rings = this.extractRings(geometry);
        if (!rings.length) {
            return '';
        }

        const allPoints = rings.flat();
        const lngs = allPoints.map((point) => point[0]);
        const lats = allPoints.map((point) => point[1]);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const lngRange = Math.max(maxLng - minLng, 1e-6);
        const latRange = Math.max(maxLat - minLat, 1e-6);

        const toX = (lng: number): number => ((lng - minLng) / lngRange) * this.viewWidth;
        const toY = (lat: number): number => (1 - (lat - minLat) / latRange) * this.viewHeight;

        return rings
            .map((ring) => ring.map((coord, index) => `${index === 0 ? 'M' : 'L'}${toX(coord[0])},${toY(coord[1])}`).join(' ') + ' Z')
            .join(' ');
    }

    private extractRings(geometry: Geometry): number[][][] {
        if (!geometry) {
            return [];
        }

        if (geometry.type === 'Polygon') {
            return geometry.coordinates as number[][][];
        }

        if (geometry.type === 'MultiPolygon') {
            return (geometry.coordinates as number[][][][]).flat();
        }

        return [];
    }
}
